import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import AuthImage from "../components/AuthImage";
import Spinner from "../components/Spinner";
import AddFootballItemModal from "../components/AddFootballItemModal";
import {PlusIcon} from "../components/icons";
import {FootballKit, FootballRefOptions, FOOTBALL_IMAGE_BASE} from "../service/footballTypes";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

const EMPTY_REFS: FootballRefOptions = {kitTypes: [], kitVersions: [], sleevesTypes: [], sizes: []};

const FootballCollectionPage: React.FC = () => {
    const navigate = useNavigate();
    const [kits, setKits] = useState<FootballKit[]>([]);
    const [refs, setRefs] = useState<FootballRefOptions>(EMPTY_REFS);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // facet filters (client-side)
    const [clubFilter, setClubFilter] = useState('');
    const [seasonFilter, setSeasonFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const api = await createAxiosInstance();
            const [k, r] = await Promise.all([
                api.get<FootballKit[]>('/football/kits'),
                api.get<FootballRefOptions>('/football/refs'),
            ]);
            setKits(k.data ?? []);
            setRefs(r.data ?? EMPTY_REFS);
        } catch (err) {
            // CTHS-148: do NOT silently fall through to an empty list — that masks 5xx as «пусто».
            console.error('Error fetching football collection:', err);
            setKits([]);
            setRefs(EMPTY_REFS);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useDocumentTitle();

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // distinct facet options derived from the loaded kits
    const clubOptions = useMemo(() => distinct(kits.map(k => k.club).filter(Boolean) as {id: string; name: string}[]), [kits]);
    const seasonOptions = useMemo(() => distinct(kits.map(k => k.season).filter(Boolean) as {id: string; name: string}[]), [kits]);
    const typeOptions = useMemo(() => {
        const present = new Set(kits.map(k => k.kitType?.id).filter(Boolean));
        return refs.kitTypes.filter(t => present.has(t.id));
    }, [kits, refs.kitTypes]);

    const filtered = useMemo(() => kits.filter(k =>
        (!clubFilter || k.club?.id === clubFilter)
        && (!seasonFilter || k.season?.id === seasonFilter)
        && (!typeFilter || k.kitType?.id === typeFilter)
    ), [kits, clubFilter, seasonFilter, typeFilter]);

    const hasFilters = clubFilter || seasonFilter || typeFilter;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Футбольная форма</h1>
                        <span className="chip">{kits.length}</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Коллекция комплектов по клубам, сезонам и типам</p>
                </div>
                {!loadError && (
                    <button type="button" onClick={() => setModalOpen(true)}
                            data-testid="football-add-kit" className="btn-primary shrink-0">
                        <PlusIcon className="w-5 h-5"/>
                        Добавить комплект
                    </button>
                )}
            </div>

            {/* facet filters */}
            <div className="flex flex-wrap items-center gap-3">
                <FacetSelect label="Клуб" value={clubFilter} onChange={setClubFilter}
                    options={clubOptions.map(o => ({value: o.id, label: o.name}))}/>
                <FacetSelect label="Сезон" value={seasonFilter} onChange={setSeasonFilter}
                    options={seasonOptions.map(o => ({value: o.id, label: o.name}))}/>
                <FacetSelect label="Тип" value={typeFilter} onChange={setTypeFilter}
                    options={typeOptions.map(o => ({value: o.id, label: o.nameRu}))}/>
                {hasFilters && (
                    <button onClick={() => { setClubFilter(''); setSeasonFilter(''); setTypeFilter(''); }}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline">
                        Сбросить
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Spinner/></div>
            ) : loadError ? (
                <div data-testid="football-error"
                     className="card p-12 text-center text-gray-500 dark:text-gray-400 space-y-4">
                    <div>Не удалось загрузить коллекцию.</div>
                    <button onClick={fetchAll} className="btn-primary">Повторить</button>
                </div>
            ) : filtered.length === 0 ? (
                <div data-testid="football-empty"
                     className="card p-12 text-center text-gray-500 dark:text-gray-400">
                    {kits.length === 0 ? 'Коллекция пуста. Добавьте первый комплект.' : 'Ничего не найдено по фильтрам.'}
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filtered.map(kit => {
                        const cover = kit.jersey?.photoUrls?.[0];
                        return (
                            <div key={kit.id}
                                 onClick={() => navigate(`/football/${kit.id}`)}
                                 className="card overflow-hidden cursor-pointer hover:shadow-card-hover transition-shadow">
                                <div className="aspect-square bg-gray-100 dark:bg-ink-700 relative">
                                    {cover ? (
                                        <AuthImage imageKey={cover} alt="" basePath={FOOTBALL_IMAGE_BASE} className="w-full h-full object-cover"/>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-4xl">👕</div>
                                    )}
                                    {kit.kitType && (
                                        <span className="badge badge-info absolute top-2 left-2">{kit.kitType.nameRu}</span>
                                    )}
                                    {kit.jersey?.number != null && (
                                        <span className="absolute top-2 right-2 text-sm font-bold px-2 py-0.5 rounded-lg bg-black/60 text-white">#{kit.jersey.number}</span>
                                    )}
                                    {kit.jersey?.version && (
                                        <span className="badge badge-info absolute bottom-2 left-2">{kit.jersey.version.nameRu}</span>
                                    )}
                                </div>
                                <div className="p-3">
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {kit.club?.name || 'Без клуба'}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {[kit.season?.name, kit.jersey?.playerName].filter(Boolean).join(' · ') || '—'}
                                    </div>
                                    {kit.shorts && (
                                        <span className="badge badge-neutral mt-2">+ шорты</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {modalOpen && (
                <AddFootballItemModal
                    refs={refs}
                    onClose={() => setModalOpen(false)}
                    onSaved={() => { setModalOpen(false); fetchAll(); }}
                />
            )}
        </div>
    );
};

function distinct(items: {id: string; name: string}[]): {id: string; name: string}[] {
    const seen = new Map<string, {id: string; name: string}>();
    items.forEach(i => { if (!seen.has(i.id)) seen.set(i.id, i); });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
}

const FacetSelect: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
}> = ({label, value, onChange, options}) => (
    <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`input-field !w-auto text-sm ${value ? 'border-ink-900 dark:border-white font-medium' : ''}`}
        disabled={options.length === 0}
    >
        <option value="">{label}: все</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

export default FootballCollectionPage;
